import { useEffect, useMemo, useState } from "react";
import { Rider } from "../../types/model";
import { getRiderById } from "../../services/riderService";
import { getRiderHistory } from "../../services/resultService";
import {
	buildSeasonOptions,
	getSeasonHistory,
	RiderHistoryItem,
	SeasonOption,
} from "./athleteProfileUtils";

type UseAthleteProfileDataInput = {
	riderId?: string;
	enabled?: boolean;
	initialRider?: Rider | null;
	initialHistory?: RiderHistoryItem[];
};

const toHistoryArray = (value: unknown): RiderHistoryItem[] => {
	if (!Array.isArray(value)) {
		return [];
	}
	return value as RiderHistoryItem[];
};

export const useAthleteProfileData = ({
	riderId,
	enabled = true,
	initialRider,
	initialHistory,
}: UseAthleteProfileDataInput) => {
	const [rider, setRider] = useState<Rider | null>(initialRider ?? null);
	const [history, setHistory] = useState<RiderHistoryItem[]>(
		toHistoryArray(initialHistory),
	);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [selectedSeasonKey, setSelectedSeasonKey] = useState("");

	useEffect(() => {
		setRider(initialRider ?? null);
		setHistory(toHistoryArray(initialHistory));
	}, [riderId, initialRider, initialHistory]);

	useEffect(() => {
		if (!enabled || !riderId) {
			return;
		}

		let isCancelled = false;

		const fetchProfile = async () => {
			setIsLoading(true);
			setError("");

			try {
				const shouldFetchHistory = !initialHistory || initialHistory.length === 0;
				const [riderData, historyData] = await Promise.all([
					getRiderById(riderId),
					shouldFetchHistory
						? getRiderHistory(riderId)
						: Promise.resolve(toHistoryArray(initialHistory)),
				]);

				if (isCancelled) {
					return;
				}

				setRider((riderData as Rider) ?? null);
				setHistory(toHistoryArray(historyData));
			} catch (fetchError) {
				if (isCancelled) {
					return;
				}
				setError("Unable to load athlete profile right now.");
			} finally {
				if (!isCancelled) {
					setIsLoading(false);
				}
			}
		};

		fetchProfile();

		return () => {
			isCancelled = true;
		};
	}, [enabled, riderId, initialHistory]);

	const seasonOptions = useMemo<SeasonOption[]>(() => buildSeasonOptions(history), [history]);

	useEffect(() => {
		if (seasonOptions.length === 0) {
			setSelectedSeasonKey("");
			return;
		}

		setSelectedSeasonKey((currentValue) => {
			if (seasonOptions.some((seasonOption) => seasonOption.key === currentValue)) {
				return currentValue;
			}
			return seasonOptions[0].key;
		});
	}, [seasonOptions]);

	const seasonHistory = useMemo(
		() => getSeasonHistory(history, selectedSeasonKey),
		[history, selectedSeasonKey],
	);

	return {
		rider,
		history,
		seasonHistory,
		seasonOptions,
		selectedSeasonKey,
		setSelectedSeasonKey,
		isLoading,
		error,
	};
};
